import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Shield, CreditCard, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRateLimit } from '@/hooks/useRateLimit';

interface PaymentFormProps {
  amount: number;
  currency?: string;
  guestEmail?: string;
  orderData?: any;
  onSuccess?: (transactionId: string) => void;
  onError?: (error: string) => void;
  onPaymentStarted?: () => void;
  onPaymentEnded?: () => void;
  onTermsChange?: (agreed: boolean) => void;
  isGuest?: boolean;
  onOpenTermsModal?: () => void;
  onBack?: () => void;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  currency = 'ILS',
  guestEmail,
  orderData,
  onSuccess,
  onError,
  onPaymentStarted,
  onPaymentEnded,
  onTermsChange,
  isGuest = false,
  onOpenTermsModal,
  onBack
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [paymentIframeUrl, setPaymentIframeUrl] = useState<string | null>(null);
  const [paymentTraceId, setPaymentTraceId] = useState<string | null>(null);
  const { user, isAdmin } = useAuth();
  const { checkRateLimit, recordAttempt } = useRateLimit('payment');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Prevent double-click payments
  const [paymentStarted, setPaymentStarted] = useState(false);

  // Listen for iframe navigation changes to detect payment completion
  useEffect(() => {
    if (!paymentIframeUrl || !iframeRef.current) return;

    let pollCount = 0;
    const maxPolls = 120; // 2 minutes max

    const checkIframeNavigation = () => {
      try {
        const iframe = iframeRef.current;
        if (!iframe) return;

        // Try to access iframe location - this will work when redirected to same origin
        try {
          const iframeUrl = iframe.contentWindow?.location?.href;

          if (iframeUrl && iframeUrl.includes('/payment/success')) {
            // console.log('Payment success detected via iframe navigation');

            // Extract orderId from URL if present
            const urlObj = new URL(iframeUrl);
            const detectedOrderId = urlObj.searchParams.get('orderId');

            // Hide iframe and redirect parent window
            setPaymentIframeUrl(null);
            setPaymentStarted(false);
            onPaymentEnded?.();

            toast.success("תשלום הושלם בהצלחה!", {
              description: "מעביר אותך לדף האישור...",
            });

            onSuccess?.(detectedOrderId || createdOrderId || paymentTraceId || '');

            // Instead of redirecting from within iframe, let the payment success page handle it
            // The PaymentSuccess page will detect iframe context and redirect parent window
            // console.log('Payment success detected - letting success page handle parent redirect');

            return;
          }

          if (iframeUrl && iframeUrl.includes('/payment/cancel')) {
            // console.log('Payment cancellation detected via iframe navigation');

            setPaymentIframeUrl(null);
            setPaymentStarted(false);
            onPaymentEnded?.();

            setError('התשלום בוטל או נכשל. אנא נסה שוב.');
            toast.error("תשלום בוטל", {
              description: "התשלום בוטל או נכשל. אנא נסה שוב.",
            });

            onError?.('התשלום בוטל');
            return;
          }
        } catch (e) {
          // Cross-origin error is expected when iframe is on payment gateway
          // This is normal and doesn't indicate an error
        }

        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(checkIframeNavigation, 1000); // Check every second
        } else {
          // Timeout after 2 minutes
          // console.log('Payment polling timeout');
          setError('זמן התשלום פג. אנא נסה שוב.');
          handleClosePayment();
        }
      } catch (error) {
        console.error('Error checking iframe navigation:', error);
      }
    };

    // Start polling after a short delay
    const timer = setTimeout(checkIframeNavigation, 2000);

    return () => clearTimeout(timer);
  }, [paymentIframeUrl, paymentTraceId, createdOrderId, onSuccess, onError, onPaymentEnded, toast]);

  const formatAddress = (address: any) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    const parts = [
      address.street_name && address.street_number ? `${address.street_name} ${address.street_number}` : address.street_name,
      address.city,
      address.details
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handlePayment = async () => {
    // Check if payment already started
    if (paymentStarted) {
      // console.log('Payment already in progress, ignoring duplicate click');
      return;
    }

    if (!orderData?.agreeTo) {
      setError('יש לאשר את התנאים וההגבלות לפני ביצוע התשלום');
      toast.error("שגיאה בתשלום", {
        description: "יש לאשר את התנאים וההגבלות לפני ביצוע התשלום",
      });
      return;
    }

    // Rate limiting check - bypass for admins
    if (!isAdmin && !checkRateLimit()) {
      setError('יותר מדי ניסיונות תשלום. אנא נסה שוב בעוד כמה דקות.');
      toast.error("יותר מדי ניסיונות", {
        description: "אנא נסה שוב בעוד כמה דקות",
      });
      return;
    }

    setPaymentStarted(true);
    setIsProcessing(true);
    setError(null);
    onPaymentStarted?.();

    try {
      // console.log('Starting payment flow - creating order first');

      // STEP 1: Pre-create order with snapshot data before payment
      let orderId = createdOrderId;

      // Define snapshot based on current data
      const orderSnapshot = {
        items: orderData.checkoutItems?.map((item: any) => ({
          id: item.id,
          name: item.name || item.product?.name,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          total_price: item.unitPrice * item.quantity,
          product_id: item.product?.id,
          delivery_option: orderData.deliveryChoices?.[item.id] === 'pickup' ? 'pickup_only' : 'delivery_only',
          delivery_cost: orderData.deliveryCosts?.[item.id] || 0
        })) || [],
        customer: {
          name: `${orderData.formData?.firstName || ''} ${orderData.formData?.lastName || ''}`.trim(),
          email: guestEmail || user?.email || orderData.formData?.email || '',
          phone: orderData.formData?.phone || ''
        },
        shipping: orderData.selectedShippingAddress,
        billing: orderData.selectedBillingAddress,
        total_amount: amount,
        currency: currency || 'ILS',
        notes: orderData.formData?.notes || ''
      };

      if (!orderId) {
        // Only create order client-side if User is authenticated (RLS allows it)
        // For Guests, we let the backend create it to bypass RLS restrictions
        if (!isGuest) {
          // Create main order with pending status
          const { data: order, error: orderError } = await supabase
            .from('orders')
            .insert({
              user_id: user?.id || null,
              total_amount: amount,
              total_delivery_cost: orderSnapshot.items.reduce((sum, item) => sum + (item.delivery_cost || 0), 0),
              status: 'pending',
              billing_address: {
                ...(orderSnapshot.billing || {}),
                first_name: orderData.formData?.firstName || orderSnapshot.customer.name.split(' ')[0] || '',
                last_name: orderData.formData?.lastName || orderSnapshot.customer.name.split(' ').slice(1).join(' ') || '',
                email: orderSnapshot.customer.email,
                phone: orderSnapshot.customer.phone
              }
            })
            .select()
            .single();

          if (orderError) {
            console.error('Failed to create order locally:', orderError);
            // Don't throw here, try backend creation as fallback? 
            // Or throw if RLS error? 
            // If it fails here, we can actually just let the backend try!
            // So we'll log but NOT throw, and let orderId fall through as null
          } else {
            // console.log('Order created successfully locally:', order.id);
            orderId = order.id;
            setCreatedOrderId(orderId);
            sessionStorage.setItem('pending_order_id', orderId);

            // Create sub-orders (Best effort)
            const subOrdersToInsert = orderSnapshot.items.map(item => ({
              order_id: order.id,
              product_id: item.product_id,
              product_name: item.name,
              quantity: item.quantity,
              unit_price: item.unit_price,
              total_price: item.total_price,
              delivery_option: item.delivery_option,
              delivery_cost: item.delivery_cost,
              delivery_address: item.delivery_option === 'delivery_only' ? formatAddress(orderSnapshot.shipping) : null,
              status: 'pending'
            }));

            const { error: subOrdersError } = await supabase
              .from('sub_orders')
              .insert(subOrdersToInsert);

            if (subOrdersError) {
              console.error('Failed to create sub-orders locally:', subOrdersError);
            }
          }
        }
      }

      // STEP 2: Create payment session
      // TODO: Add feature flag check for Grow vs Invoice4U
      // const growEnabled = import.meta.env.VITE_GROW_PAYMENTS_ENABLED === 'true';
      // const paymentFunction = growEnabled ? 'grow-create-payment' : 'invoice4u-create-payment';
      
      // For now, use Invoice4U (Grow migration in progress)
      // console.log('Calling new Invoice4U create-payment with order ID:', orderId);

      const { data: paymentResult, error: paymentError } = await supabase.functions.invoke(
        'invoice4u-create-payment', // TODO: Replace with feature flag logic above
        {
          body: {
            orderId, // If null (Guest or failed local), backend will create it
            orderSnapshot: !orderId ? orderSnapshot : undefined, // Pass snapshot if needed
            guestInfo: {
              firstName: orderData.formData?.firstName,
              lastName: orderData.formData?.lastName,
              email: guestEmail || user?.email || orderData.formData?.email,
              phone: orderData.formData?.phone
            }
          }
        }
      );

      // Record payment attempt for rate limiting
      recordAttempt();

      // Check for both network/invocation errors AND application-level errors (returned as 200 OK)
      if (paymentError) {
        console.error('Payment invocation error:', paymentError);
        throw new Error(paymentError.message || 'שגיאה בתקשורת עם השרת');
      }

      // If we have a redirectUrl, treat it as a success regardless of the 'success' property
      // (The backend validation returns { success: false, error: ... } for errors, but just the data object for success)
      if (paymentResult && !paymentResult.success && !paymentResult.redirectUrl) {
        console.error('Payment API returned error result:', paymentResult); // Log full object
        throw new Error(paymentResult.error || 'שגיאה ביצירת התשלום');
      }

      if (paymentResult.redirectUrl && !paymentResult.redirectUrl.startsWith('http')) {
        console.error('Invalid redirect URL received:', paymentResult.redirectUrl);
        throw new Error('התקבל קישור תשלום לא תקין מהמערכת');
      }

      if (!paymentResult || !paymentResult.redirectUrl) {
        console.error('Invalid payment result:', paymentResult);
        throw new Error('שירות התשלומים לא החזיר כתובת לתשלום. אנא נסה שוב.');
      }

      // STEP 3: Show payment in iframe instead of redirecting
      // console.log('Payment session created, showing iframe with URL:', paymentResult.redirectUrl);

      setPaymentIframeUrl(paymentResult.redirectUrl);
      setPaymentTraceId(paymentResult.traceId);
      setIsProcessing(false); // Stop the loading state once iframe is ready

    } catch (err) {
      console.error('Payment error:', err);

      // If order was created, update its status to cancelled
      if (createdOrderId) {
        try {
          await supabase
            .from('orders')
            .update({ status: 'cancelled' })
            .eq('id', createdOrderId);
        } catch (updateError) {
          console.error('Failed to update order status to cancelled:', updateError);
        }
      }

      // Determine user-friendly error message
      let userErrorMessage = 'שגיאה בעיבוד התשלום. אנא נסה שוב.';

      if (err instanceof Error) {
        // Check for Invoice4U authorization errors
        if (err.message.includes('UnauthorizedUser') || err.message.includes('Unauthorized')) {
          userErrorMessage = 'בעיית הרשאה עם ספק התשלומים. אנא צור קשר עם התמיכה.';
        } else if (err.message.includes('נמוך מדי')) {
          userErrorMessage = 'סכום התשלום נמוך מהמינימום הנדרש. נא להוסיף פריטים נוספים להזמנה.';
        } else if (err.message.includes('API key not configured')) {
          userErrorMessage = 'שירות התשלומים אינו זמין כרגע. אנא נסה מאוחר יותר.';
        } else if (err.message.includes('שגיאה ביצירת ההזמנה')) {
          userErrorMessage = err.message;
        }
      }

      setError(userErrorMessage);
      onError?.(userErrorMessage);
      onPaymentEnded?.();

      toast.error("שגיאה בתשלום", {
        description: userErrorMessage,
      });

      setIsProcessing(false);
      setPaymentStarted(false);
    }
  };

  const handleClosePayment = () => {
    setPaymentIframeUrl(null);
    setPaymentTraceId(null);
    setPaymentStarted(false);
    onPaymentEnded?.();

    // Show a message that payment was cancelled
    toast("תשלום בוטל", {
      description: "התשלום בוטל על ידי המשתמש. ניתן לנסות שוב.",
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Default payment form UI
  return (
    <Card className="w-full mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <CreditCard className="h-5 w-5" />
          <span>סכום לתשלום</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex justify-center items-center">
            <span className="text-2xl font-bold">{formatAmount(amount, currency)}</span>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!paymentIframeUrl ? (
          <div className="space-y-4">
            {/* Terms Agreement */}
            <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/30">
              <Checkbox
                id="terms"
                checked={orderData?.agreeTo || false}
                onCheckedChange={(checked) => onTermsChange?.(Boolean(checked))}
              />
              <label htmlFor="terms" className="text-sm cursor-pointer">
                אני מסכים ל
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lime underline cursor-pointer hover:text-lime/80 transition-colors mx-1"
                >
                  תנאי השימוש
                </a>
              </label>
            </div>

            <div className="flex gap-4 items-center">
              {onBack && (
                <button
                  onClick={onBack}
                  className="text-foreground hover:text-foreground/80 transition-colors flex items-center gap-1"
                >
                  <span className="text-sm">&lt;</span>
                  <span>חזור</span>
                </button>
              )}
              <Button
                onClick={handlePayment}
                disabled={isProcessing || paymentStarted}
                className="flex-1 bg-lime text-lime-foreground hover:bg-lime/90"
                size="lg"
              >
                {isProcessing ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    אנא המתן...
                  </span>
                ) : (
                  'שלם עכשיו'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium">תשלום מאובטח בתהליך</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClosePayment}
                className="h-8"
              >
                ביטול
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <iframe
                ref={iframeRef}
                src={paymentIframeUrl}
                className="w-full h-[900px] md:h-[700px] border-0"
                title="תשלום מאובטח"
                sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-top-navigation-by-user-activation"
              />
            </div>
          </div>
        )}

        {!paymentIframeUrl && (
          <div className="text-xs text-muted-foreground text-center">
            התשלום מאובטח עם הצפנה SSL. איננו שומרים פרטי כרטיס אשראי.
            {/* TODO: Update text when Grow is enabled: "התשלום מאובטח באמצעות Grow" */}
          </div>
        )}
      </CardContent>
    </Card>
  );
};